import { dashboardService as firebaseDashboardService, userService as firebaseUserService } from './firebaseService';
import { supabaseService } from './supabaseService';

// Hybrid Dashboard Configuration
const HYBRID_CONFIG = {
  SUPABASE_FIRST: true, // Önce Supabase'i dene
  TIMEOUT_MS: 15000, // 15 saniye timeout (5000'den arttırıldı)
  RETRY_COUNT: 1, // Tekrar deneme sayısı
  FALLBACK_TO_FIREBASE: true, // Firebase fallback aktif
  DEBUG: true, // Debug mode
};

// Helper function for timeout with debug
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  if (HYBRID_CONFIG.DEBUG) {
    console.log(`⏱️ HYBRID DEBUG: Timeout başlatıldı (${timeoutMs}ms)`);
  }
  
  return Promise.race([
    promise.then(result => {
      if (HYBRID_CONFIG.DEBUG) {
        console.log('✅ HYBRID DEBUG: Promise başarıyla tamamlandı');
      }
      return result;
    }).catch(error => {
      if (HYBRID_CONFIG.DEBUG) {
        console.log('❌ HYBRID DEBUG: Promise hata ile tamamlandı:', error.message);
      }
      throw error;
    }),
    new Promise<never>((_, reject) => 
      setTimeout(() => {
        if (HYBRID_CONFIG.DEBUG) {
          console.log(`⏰ HYBRID DEBUG: Timeout tetiklendi (${timeoutMs}ms)`);
        }
        reject(new Error(`Operation timeout after ${timeoutMs}ms`));
      }, timeoutMs)
    )
  ]);
};

export interface HybridDashboardStats {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newFemaleUsersToday: number;
  newMaleUsersToday: number;
  premiumUsers: number;
  pendingGiftCardApplications: number;
  dailyRevenue: number;
  monthlyRevenue: number;
  totalRevenue: number;
  voiceCallDailyRevenue: number;
  voiceCallDailySalesCount: number;
  totalMatches: number;
  dataSource: 'supabase' | 'firebase' | 'hybrid';
  errors?: string[];
}

export const hybridDashboardService = {
  // 🔄 HYBRID: Get total users count
  async getTotalUsersCount(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Toplam kullanıcı sayısı getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const count = await Promise.race([
          supabaseService.getTotalUsersCount(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Toplam kullanıcı:', count);
        return { count, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const count = await firebaseUserService.getTotalUsersCount();
            console.log('✅ HYBRID: Firebase fallback başarılı - Toplam kullanıcı:', count);
            return { count, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      // Firebase first mode (currently not used but could be toggled)
      try {
        const count = await firebaseUserService.getTotalUsersCount();
        return { count, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const count = await supabaseService.getTotalUsersCount();
        return { count, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get active users count
  async getActiveUsersCount(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Aktif kullanıcı sayısı getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const count = await Promise.race([
          supabaseService.getActiveUsersCount(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Aktif kullanıcı:', count);
        return { count, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const count = await firebaseUserService.getActiveUsersCount();
            console.log('✅ HYBRID: Firebase fallback başarılı - Aktif kullanıcı:', count);
            return { count, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const count = await firebaseUserService.getActiveUsersCount();
        return { count, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const count = await supabaseService.getActiveUsersCount();
        return { count, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get today's new users count
  async getTodayNewUsersCount(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Bugünkü yeni kullanıcı sayısı getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const count = await Promise.race([
          supabaseService.getTodayNewUsersCount(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Bugünkü yeni kullanıcı:', count);
        return { count, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const count = await firebaseUserService.getTodayNewUsers();
            console.log('✅ HYBRID: Firebase fallback başarılı - Bugünkü yeni kullanıcı:', count);
            return { count, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const count = await firebaseUserService.getTodayNewUsers();
        return { count, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const count = await supabaseService.getTodayNewUsersCount();
        return { count, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get today's new users count by gender (Supabase only for now)
  async getTodayNewUsersCountByGender(): Promise<{ female: number; male: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Bugünkü yeni kullanıcılar (cinsiyet) getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const result = await Promise.race([
          supabaseService.getTodayNewUsersCountByGender(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        return { female: result.female || 0, male: result.male || 0, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız (cinsiyet), Firebase\'e geçiliyor...', error);
        // Firebase tarafında bu metrik yoksa 0 dön
        return { female: 0, male: 0, source: 'firebase' };
      }
    }
    
    return { female: 0, male: 0, source: 'firebase' };
  },

  // 🔄 HYBRID: Get premium users count
  async getPremiumUsersCount(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Premium kullanıcı sayısı getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const count = await Promise.race([
          supabaseService.getPremiumUsersCount(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Premium kullanıcı:', count);
        return { count, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const count = await firebaseDashboardService.getPremiumUsersCount();
            console.log('✅ HYBRID: Firebase fallback başarılı - Premium kullanıcı:', count);
            return { count, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const count = await firebaseDashboardService.getPremiumUsersCount();
        return { count, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const count = await supabaseService.getPremiumUsersCount();
        return { count, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get recent users
  async getRecentUsers(count: number = 5): Promise<{ users: any[]; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Son kullanıcılar getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const users = await Promise.race([
          supabaseService.getRecentUsers(count),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Son kullanıcılar:', users?.length || 0);
        return { users, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const users = await firebaseDashboardService.getRecentUsers(count);
            console.log('✅ HYBRID: Firebase fallback başarılı - Son kullanıcılar:', users?.length || 0);
            return { users, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const users = await firebaseDashboardService.getRecentUsers(count);
        return { users, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const users = await supabaseService.getRecentUsers(count);
        return { users, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get total matches count
  async getTotalMatchesCount(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Toplam eşleşme sayısı getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const count = await Promise.race([
          supabaseService.getTotalMatchesCount(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Toplam eşleşme:', count);
        return { count, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const count = await firebaseDashboardService.getTotalMatchesCount();
            console.log('✅ HYBRID: Firebase fallback başarılı - Toplam eşleşme:', count);
            return { count, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const count = await firebaseDashboardService.getTotalMatchesCount();
        return { count, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const count = await supabaseService.getTotalMatchesCount();
        return { count, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get daily revenue
  async getDailyRevenue(): Promise<{ revenue: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Günlük gelir getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const revenue = await Promise.race([
          supabaseService.getDailyRevenue(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Günlük gelir:', revenue);
        return { revenue, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const stats = await firebaseDashboardService.getDashboardStats();
            const revenue = stats.dailyRevenue || 0;
            console.log('✅ HYBRID: Firebase fallback başarılı - Günlük gelir:', revenue);
            return { revenue, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const stats = await firebaseDashboardService.getDashboardStats();
        const revenue = stats.dailyRevenue || 0;
        return { revenue, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const revenue = await supabaseService.getDailyRevenue();
        return { revenue, source: 'supabase' };
      }
    }
  },

  // 🎙️ HYBRID: Sesli Arama Paketi (premium_plus_subscription) günlük satış/gelir
  async getVoiceCallDailyStats(): Promise<{ revenue: number; count: number; source: 'supabase' | 'firebase' }> {
    const productType = 'premium_plus_subscription';
    console.log('🔄 HYBRID: Sesli Arama Paketi günlük istatistikleri getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const result = await Promise.race([
          supabaseService.getDailyRevenueByProductType(productType),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        return { revenue: result.revenue || 0, count: result.count || 0, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız (Sesli Arama), Firebase\'e geçiliyor...', error);
        // Firebase tarafında satış tipine göre ayrıştırma yoksa 0 dön
        return { revenue: 0, count: 0, source: 'firebase' };
      }
    }
    
    // Firebase first mode (şimdilik kullanılmıyor)
    return { revenue: 0, count: 0, source: 'firebase' };
  },

  // 🔄 HYBRID: Get monthly revenue
  async getMonthlyRevenue(): Promise<{ revenue: number; source: 'supabase' | 'firebase' }> {
    console.log('🔄 HYBRID: Aylık gelir getiriliyor...');
    
    if (HYBRID_CONFIG.SUPABASE_FIRST) {
      try {
        console.log('🎯 HYBRID: Önce Supabase deneniyor...');
        const revenue = await Promise.race([
          supabaseService.getMonthlyRevenue(),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
          )
        ]);
        
        console.log('✅ HYBRID: Supabase başarılı - Aylık gelir:', revenue);
        return { revenue, source: 'supabase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Supabase başarısız, Firebase\'e geçiliyor...', error);
        
        if (HYBRID_CONFIG.FALLBACK_TO_FIREBASE) {
          try {
            const stats = await firebaseDashboardService.getDashboardStats();
            const revenue = stats.monthlyRevenue || 0;
            console.log('✅ HYBRID: Firebase fallback başarılı - Aylık gelir:', revenue);
            return { revenue, source: 'firebase' };
          } catch (firebaseError) {
            console.error('❌ HYBRID: Firebase de başarısız:', firebaseError);
            throw new Error('Both Supabase and Firebase failed');
          }
        } else {
          throw error;
        }
      }
    } else {
      try {
        const stats = await firebaseDashboardService.getDashboardStats();
        const revenue = stats.monthlyRevenue || 0;
        return { revenue, source: 'firebase' };
      } catch (error) {
        console.warn('⚠️ HYBRID: Firebase başarısız, Supabase\'e geçiliyor...', error);
        const revenue = await supabaseService.getMonthlyRevenue();
        return { revenue, source: 'supabase' };
      }
    }
  },

  // 🔄 HYBRID: Get total revenue
  async getTotalRevenue(): Promise<{ revenue: number; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Toplam gelir verisi alınıyor...');
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const revenue = await withTimeout(
            supabaseService.getTotalRevenue(),
            HYBRID_CONFIG.TIMEOUT_MS
          );
          
          console.log('✅ HYBRID: Toplam gelir Supabase\'den alındı:', revenue);
          return { revenue, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase toplam gelir hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback
      const dashboardStats = await firebaseDashboardService.getDashboardStats();
      const revenue = dashboardStats.totalRevenue || 0;
      console.log('🔴 HYBRID: Toplam gelir Firebase\'den alındı:', revenue);
      return { revenue, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Toplam gelir alınırken hata:', error);
      return { revenue: 0, source: 'firebase' };
    }
  },

  // 🔄 HYBRID: Get pending gift card applications
  async getPendingGiftCardApplications(): Promise<{ count: number; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Bekleyen hediye kartı başvuruları alınıyor...');
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const count = await Promise.race([
            supabaseService.getPendingGiftCardApplications(),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Hediye kartı başvuruları Supabase\'den alındı:', count);
          return { count, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase hediye kartı hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback (salesService'de henüz implement edilmemiş)
      const count = 0; // Placeholder
      console.log('🔴 HYBRID: Hediye kartı başvuruları Firebase\'den alındı:', count);
      return { count, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Hediye kartı başvuruları alınırken hata:', error);
      return { count: 0, source: 'firebase' };
    }
  },

  // 🔄 HYBRID: Get comprehensive dashboard stats
  async getDashboardStats(): Promise<HybridDashboardStats> {
    console.log('🔄 HYBRID: Kapsamlı dashboard istatistikleri getiriliyor...');
    
    const errors: string[] = [];
    let dataSource: 'supabase' | 'firebase' | 'hybrid' = 'supabase';
    let fallbackUsed = false;

    try {
      // Paralel olarak tüm verileri çek
      const [
        totalUsersResult,
        activeUsersResult,
        todayNewUsersResult,
        todayNewUsersGenderResult,
        premiumUsersResult,
        totalMatchesResult,
        dailyRevenueResult,
        monthlyRevenueResult,
        totalRevenueResult,
        pendingGiftCardResult,
        voiceCallDailyResult
      ] = await Promise.allSettled([
        this.getTotalUsersCount(),
        this.getActiveUsersCount(),
        this.getTodayNewUsersCount(),
        this.getTodayNewUsersCountByGender(),
        this.getPremiumUsersCount(),
        this.getTotalMatchesCount(),
        this.getDailyRevenue(),
        this.getMonthlyRevenue(),
        this.getTotalRevenue(),
        this.getPendingGiftCardApplications(),
        this.getVoiceCallDailyStats()
      ]);

      // Fallback kullanılıp kullanılmadığını kontrol et
      const sources = [
        totalUsersResult.status === 'fulfilled' ? totalUsersResult.value.source : null,
        activeUsersResult.status === 'fulfilled' ? activeUsersResult.value.source : null,
        todayNewUsersResult.status === 'fulfilled' ? todayNewUsersResult.value.source : null,
        todayNewUsersGenderResult.status === 'fulfilled' ? todayNewUsersGenderResult.value.source : null,
        premiumUsersResult.status === 'fulfilled' ? premiumUsersResult.value.source : null,
        totalMatchesResult.status === 'fulfilled' ? totalMatchesResult.value.source : null,
        dailyRevenueResult.status === 'fulfilled' ? dailyRevenueResult.value.source : null,
        monthlyRevenueResult.status === 'fulfilled' ? monthlyRevenueResult.value.source : null,
        totalRevenueResult.status === 'fulfilled' ? totalRevenueResult.value.source : null,
        pendingGiftCardResult.status === 'fulfilled' ? pendingGiftCardResult.value.source : null,
        voiceCallDailyResult.status === 'fulfilled' ? voiceCallDailyResult.value.source : null,
      ].filter(Boolean);

      const uniqueSources = Array.from(new Set(sources));
      if (uniqueSources.length > 1) {
        dataSource = 'hybrid';
        fallbackUsed = true;
      } else if (uniqueSources.length === 1) {
        dataSource = uniqueSources[0] as 'supabase' | 'firebase';
      }

      // Sonuçları birleştir
      const stats: HybridDashboardStats = {
        totalUsers: totalUsersResult.status === 'fulfilled' ? totalUsersResult.value.count : 0,
        activeUsers: activeUsersResult.status === 'fulfilled' ? activeUsersResult.value.count : 0,
        newUsersToday: todayNewUsersResult.status === 'fulfilled' ? todayNewUsersResult.value.count : 0,
        newFemaleUsersToday: todayNewUsersGenderResult.status === 'fulfilled' ? todayNewUsersGenderResult.value.female : 0,
        newMaleUsersToday: todayNewUsersGenderResult.status === 'fulfilled' ? todayNewUsersGenderResult.value.male : 0,
        premiumUsers: premiumUsersResult.status === 'fulfilled' ? premiumUsersResult.value.count : 0,
        totalMatches: totalMatchesResult.status === 'fulfilled' ? totalMatchesResult.value.count : 0,
        dailyRevenue: dailyRevenueResult.status === 'fulfilled' ? dailyRevenueResult.value.revenue : 0,
        monthlyRevenue: monthlyRevenueResult.status === 'fulfilled' ? monthlyRevenueResult.value.revenue : 0,
        totalRevenue: totalRevenueResult.status === 'fulfilled' ? totalRevenueResult.value.revenue : 0,
        voiceCallDailyRevenue: voiceCallDailyResult.status === 'fulfilled' ? voiceCallDailyResult.value.revenue : 0,
        voiceCallDailySalesCount: voiceCallDailyResult.status === 'fulfilled' ? voiceCallDailyResult.value.count : 0,
        pendingGiftCardApplications: pendingGiftCardResult.status === 'fulfilled' ? pendingGiftCardResult.value.count : 0,
        dataSource,
        errors: errors.length > 0 ? errors : undefined
      };

      // Hata kontrolü
      [totalUsersResult, activeUsersResult, todayNewUsersResult, todayNewUsersGenderResult, premiumUsersResult, totalMatchesResult, 
       dailyRevenueResult, monthlyRevenueResult, totalRevenueResult, pendingGiftCardResult, voiceCallDailyResult]
        .forEach((result, index) => {
          if (result.status === 'rejected') {
            const fieldNames = ['totalUsers', 'activeUsers', 'newUsersToday', 'newUsersTodayByGender', 'premiumUsers', 'totalMatches',
                               'dailyRevenue', 'monthlyRevenue', 'totalRevenue', 'pendingGiftCardApplications', 'voiceCallDailyStats'];
            errors.push(`${fieldNames[index]}: ${result.reason}`);
          }
        });

      console.log('✅ HYBRID: Dashboard istatistikleri başarıyla toplandı:', {
        dataSource,
        fallbackUsed,
        errorsCount: errors.length,
        stats: {
          totalUsers: stats.totalUsers,
          activeUsers: stats.activeUsers,
          newUsersToday: stats.newUsersToday,
          premiumUsers: stats.premiumUsers,
          totalMatches: stats.totalMatches,
          dailyRevenue: stats.dailyRevenue,
          monthlyRevenue: stats.monthlyRevenue,
          totalRevenue: stats.totalRevenue
        }
      });

      return stats;
    } catch (error) {
      console.error('❌ HYBRID: Dashboard istatistikleri toplu hatası:', error);
      
      // Tamamen başarısız olursa, sadece Firebase'i dene
      console.log('🔄 HYBRID: Acil durum - Sadece Firebase kullanılıyor...');
      try {
        const firebaseStats = await firebaseDashboardService.getDashboardStats();
        const [totalMatchesResult] = await Promise.allSettled([
          firebaseDashboardService.getTotalMatchesCount()
        ]);

        return {
          ...firebaseStats,
          totalMatches: totalMatchesResult.status === 'fulfilled' ? totalMatchesResult.value : 0,
          newFemaleUsersToday: 0,
          newMaleUsersToday: 0,
          voiceCallDailyRevenue: 0,
          voiceCallDailySalesCount: 0,
          dataSource: 'firebase',
          errors: ['Supabase completely failed, using Firebase only']
        };
      } catch (firebaseError) {
        console.error('❌ HYBRID: Firebase de tamamen başarısız:', firebaseError);
        
        // Son çare - boş veriler döndür
        return {
          totalUsers: 0,
          activeUsers: 0,
          newUsersToday: 0,
          newFemaleUsersToday: 0,
          newMaleUsersToday: 0,
          premiumUsers: 0,
          totalMatches: 0,
          dailyRevenue: 0,
          monthlyRevenue: 0,
          totalRevenue: 0,
          pendingGiftCardApplications: 0,
          voiceCallDailyRevenue: 0,
          voiceCallDailySalesCount: 0,
          dataSource: 'firebase',
          errors: ['Both Supabase and Firebase completely failed']
        };
      }
    }
  },

  // 🔄 HYBRID USER MANAGEMENT OPERATIONS

  // Get all users with pagination and filters
  async getAllUsersWithPagination({ 
    limit = 50, 
    offset = 0, 
    searchQuery,
    statusFilter = 'all',
    genderFilter = 'all'
  }: { 
    limit?: number; 
    offset?: number; 
    searchQuery?: string;
    statusFilter?: string;
    genderFilter?: string;
  } = {}): Promise<{ users: any[], total: number, source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Kullanıcı listesi alınıyor...', { limit, offset, searchQuery, statusFilter, genderFilter });
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const result = await Promise.race([
            supabaseService.getAllUsersWithPagination({ 
              limit, 
              offset, 
              searchQuery,
              statusFilter,
              genderFilter
            }),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Kullanıcı listesi Supabase\'den alındı:', result.users.length, 'kullanıcı');
          return { ...result, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase kullanıcı listesi hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback
      const result = await firebaseUserService.getAllUsers();
      let filteredUsers = result.users;

      // Apply filters for Firebase data
      if (searchQuery && searchQuery.trim()) {
        const searchTerm = searchQuery.trim().toLowerCase();
        filteredUsers = filteredUsers.filter(user => {
          const nameMatch = user.name?.toLowerCase().includes(searchTerm);
          const emailMatch = user.email?.toLowerCase().includes(searchTerm);
          const idMatch = user.id?.toLowerCase().includes(searchTerm);
          return nameMatch || emailMatch || idMatch;
        });
      }

      if (statusFilter === 'active') {
        filteredUsers = filteredUsers.filter(user => user.isActive);
      } else if (statusFilter === 'inactive') {
        filteredUsers = filteredUsers.filter(user => !user.isActive);
      }

      if (genderFilter !== 'all') {
        filteredUsers = filteredUsers.filter(user => {
          const userGender = (user as any).gender;
          if (genderFilter === 'male') {
            return userGender === 'Erkek' || userGender === 'male';
          } else if (genderFilter === 'female') {
            return userGender === 'Kadın' || userGender === 'female';
          } else if (genderFilter === 'other') {
            return !userGender || (userGender !== 'Erkek' && userGender !== 'Kadın' && 
                    userGender !== 'male' && userGender !== 'female');
          }
          return false;
        });
      }

      // Sort by creation date (newest first)
      filteredUsers.sort((a, b) => {
        const dateA = a.createdAt;
        const dateB = b.createdAt;
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        try {
          const timeA = dateA.toDate ? dateA.toDate().getTime() : new Date(dateA).getTime();
          const timeB = dateB.toDate ? dateB.toDate().getTime() : new Date(dateB).getTime();
          return timeB - timeA;
        } catch (error) {
          return 0;
        }
      });

      // Apply pagination
      const total = filteredUsers.length;
      const paginatedUsers = filteredUsers.slice(offset, offset + limit);

      console.log('🔴 HYBRID: Kullanıcı listesi Firebase\'den alındı:', paginatedUsers.length, 'kullanıcı, toplam:', total);
      return { users: paginatedUsers, total, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı listesi alınırken hata:', error);
      return { users: [], total: 0, source: 'firebase' };
    }
  },

  // Update user status
  async updateUserStatus(userId: string, isActive: boolean): Promise<{ success: boolean; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Kullanıcı durumu güncelleniyor...', { userId, isActive });
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const success = await Promise.race([
            supabaseService.updateUserStatus(userId, isActive),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Kullanıcı durumu Supabase\'de güncellendi');
          return { success, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase kullanıcı durumu güncelleme hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback
      const success = await firebaseUserService.updateUserStatus(userId, isActive);
      console.log('🔴 HYBRID: Kullanıcı durumu Firebase\'de güncellendi');
      return { success, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı durumu güncellenirken hata:', error);
      return { success: false, source: 'firebase' };
    }
  },

  // Get user by ID
  async getUserById(userId: string): Promise<{ user: any | null; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Kullanıcı detayları alınıyor...', userId);
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const user = await Promise.race([
            supabaseService.getUserById(userId),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Kullanıcı detayları Supabase\'den alındı');
          return { user, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase kullanıcı detay hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback
      const user = await firebaseUserService.getUserById(userId);
      console.log('🔴 HYBRID: Kullanıcı detayları Firebase\'den alındı');
      return { user, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı detayları alınırken hata:', error);
      return { user: null, source: 'firebase' };
    }
  },

  // Update user profile
  async updateUserProfile(userId: string, updates: any): Promise<{ success: boolean; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Kullanıcı profili güncelleniyor...', { userId, updates });
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const success = await Promise.race([
            supabaseService.updateUserProfile(userId, updates),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Kullanıcı profili Supabase\'de güncellendi');
          return { success, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase kullanıcı profil güncelleme hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback (userService'de henüz implement edilmemiş olabilir)
      // Firebase equivalent işlemi burada yapılabilir
      console.log('🔴 HYBRID: Firebase kullanıcı profil güncelleme henüz implement edilmedi');
      return { success: false, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı profili güncellenirken hata:', error);
      return { success: false, source: 'firebase' };
    }
  },

  // Delete user (soft delete)
  async deleteUser(userId: string): Promise<{ success: boolean; source: 'supabase' | 'firebase' }> {
    try {
      console.log('🔄 HYBRID: Kullanıcı siliniyor...', userId);
      
      if (HYBRID_CONFIG.SUPABASE_FIRST) {
        try {
          const success = await Promise.race([
            supabaseService.deleteUser(userId),
            new Promise<never>((_, reject) => 
              setTimeout(() => reject(new Error('Supabase timeout')), HYBRID_CONFIG.TIMEOUT_MS)
            )
          ]);
          
          console.log('✅ HYBRID: Kullanıcı Supabase\'de silindi');
          return { success, source: 'supabase' };
        } catch (error) {
          console.warn('⚠️ HYBRID: Supabase kullanıcı silme hatası, Firebase\'e geçiliyor:', error);
        }
      }

      // Firebase fallback
      const success = await firebaseUserService.updateUserStatus(userId, false); // Soft delete as deactivation
      console.log('🔴 HYBRID: Kullanıcı Firebase\'de silindi (pasifleştirildi)');
      return { success, source: 'firebase' };
    } catch (error) {
      console.error('❌ HYBRID: Kullanıcı silinirken hata:', error);
      return { success: false, source: 'firebase' };
    }
  }
}; 